<script setup>
import { computed, ref, watch } from "vue";
import { stopStore } from "@/_store/stopStore";
import ArgonTd from "@/components/table/ArgonTd.vue";
import SimpleTable from "@/components/table/SimpleTable.vue";
import moment from "moment";

const props = defineProps({
  stopId: {
    type: String,
    default: "",
  },
  isActive: {
    type: Boolean,
    default: false,
  },
  height: {
    type: Number,
    required: true,
  },
});

const stopsStore = stopStore();
const isLoading = ref(false);
const lastLoadedStopId = ref("");
const pendingStopId = ref("");

const historyHeaders = [
  { header: "Modificaciones", field: "created_at" },
  { header: "", field: "changes" },
];

function formatEstadoLabel(value) {
  const text = String(value ?? "").trim();
  if (!text) return "-";
  return text.replace(/_/g, " ");
}

function formatChanges(value) {
  const auditValue =
    value && typeof value === "object" && !Array.isArray(value) ? value : {};
  const changes = [];

  const fecha = String(auditValue?.fecha ?? "").trim();
  if (fecha) changes.push(`Cambió la fecha al ${moment(fecha).format("DD/MM/YYYY")}`);

  const direccion = String(auditValue?.direccion ?? "").trim();
  if (direccion) changes.push(`Cambió la dirección a ${direccion}`);

  const estado = String(auditValue?.estado ?? "").trim();
  if (estado) changes.push(`Cambió el estado a ${formatEstadoLabel(estado)}`);

  if (changes.length === 0) return "sin cambios";
  if (changes.length === 1) return changes[0];
  return changes.join(" y ");
}

const historyRows = computed(() => {
  const data = Array.isArray(stopsStore.stopAudit) ? stopsStore.stopAudit : [];

  return {
    total: data.length,
    data,
  };
});

function getPosition(field) {
  const column = historyHeaders.find((col) => col.field === field);
  return column?.position || "start";
}

const emptyMessage = computed(() => {
  if (isLoading.value) return "Cargando historial...";
  if (stopsStore.error) return "No se pudo cargar el historial";
  return "Sin historial";
});

function loadHistoryIfNeeded(stopIdValue) {
  const normalizedStopId = String(stopIdValue ?? "").trim();
  if (!normalizedStopId || isLoading.value) return;
  if (lastLoadedStopId.value === normalizedStopId) return;

  pendingStopId.value = normalizedStopId;
  isLoading.value = true;
  stopsStore.getStopAudit(normalizedStopId);
}

watch(
  [() => props.stopId, () => props.isActive],
  ([stopIdValue, isActive]) => {
    if (!isActive) return;
    loadHistoryIfNeeded(stopIdValue);
  },
  { immediate: true }
);

watch(
  () => stopsStore.stopAudit,
  (value) => {
    if (!isLoading.value) return;
    if (!Array.isArray(value)) return;
    lastLoadedStopId.value = pendingStopId.value;
    pendingStopId.value = "";
    isLoading.value = false;
  }
);

watch(
  () => stopsStore.error,
  (value) => {
    if (!isLoading.value || !value) return;
    pendingStopId.value = "";
    lastLoadedStopId.value = "";
    isLoading.value = false;
  }
);
</script>

<template>
  <simple-table
    :headers="historyHeaders"
    :items="historyRows"
    :empty-message="emptyMessage"
    :show-line-separator="true"
    :height="height"
  >
    <template #default="{ item }">
      <argon-td
        :text-primary="moment.utc(item.created_at).local().format('DD/MM/YYYY HH:mm')"
        :text-secondary="moment.utc(item.created_at).local().fromNow()"
        :position="getPosition('created_at')"
      />
      <argon-td
        :text-primary="formatChanges(item?.value)"
        :position="getPosition('changes')"
      />
    </template>
  </simple-table>
</template>
